<div class="container">
  <app-section-header
    title="Transformation History"
    subtitle="Every transformation run with its status, timing, and (on expand) the input and output payloads."
  >
    <button pButton type="button" label="Refresh" icon="pi pi-refresh" severity="primary" (click)="load()"></button>
  </app-section-header>

  <!-- Filters -->
  <div class="filter-bar">
    <div class="filter-group">
      <label for="filterStatus">Status</label>
      <select id="filterStatus" [(ngModel)]="filterStatus" (change)="load()">
        <option value="">All</option>
        <option value="Success">Success</option>
        <option value="Warning">Warning</option>
        <option value="PartialSuccess">Partial Success</option>
        <option value="Error">Error</option>
      </select>
    </div>
    <div class="filter-group">
      <label for="filterTemplateId">Template</label>
      <input
        id="filterTemplateId"
        type="text"
        [(ngModel)]="filterTemplateId"
        placeholder="Filter by template ID…"
        (keyup.enter)="load()"
      />
    </div>
    <div class="filter-group">
      <label for="filterLimit">Limit</label>
      <select id="filterLimit" [(ngModel)]="filterLimit" (change)="load()">
        <option [ngValue]="25">25</option>
        <option [ngValue]="50">50</option>
        <option [ngValue]="100">100</option>
        <option [ngValue]="250">250</option>
      </select>
    </div>
    <div class="filter-group">
      <label for="filterFrom">From</label>
      <input id="filterFrom" type="date" [(ngModel)]="filterFrom" (change)="load()" />
    </div>
    <div class="filter-group">
      <label for="filterTo">To</label>
      <input id="filterTo" type="date" [(ngModel)]="filterTo" (change)="load()" />
    </div>
  </div>

  <!-- Summary cards -->
  <div class="stat-cards">
    <div class="stat-card">
      <span class="stat-num">{{ total }}</span>
      <span class="stat-label">Total</span>
    </div>
    <div class="stat-card success">
      <span class="stat-num">{{ counts['Success'] || 0 }}</span>
      <span class="stat-label">Success</span>
    </div>
    <div class="stat-card warning">
      <span class="stat-num">{{ counts['Warning'] || 0 }}</span>
      <span class="stat-label">Warning</span>
    </div>
    <div class="stat-card partial">
      <span class="stat-num">{{ counts['PartialSuccess'] || 0 }}</span>
      <span class="stat-label">Partial</span>
    </div>
    <div class="stat-card error">
      <span class="stat-num">{{ counts['Error'] || 0 }}</span>
      <span class="stat-label">Error</span>
    </div>
    <div class="stat-card neutral">
      <span class="stat-num">{{ avgMs }}</span>
      <span class="stat-label">Avg ms</span>
    </div>
  </div>

  @if (error) {
    <div class="alert alert-error">{{ error }}</div>
  }

  <!-- Grid -->
  <app-data-table
    [rows]="logs"
    [columns]="logColumns"
    [loading]="loading"
    dataKey="id"
    emptyIcon="pi-history"
    emptyHeading="No transformation logs"
    emptyMessage='Run a transformation on the "Test Transform" tab to generate logs.'
  >
    <ng-template #row let-log>
      <tr class="summary-row" (click)="toggleDetail(log)" [class.expanded]="selectedId === log.id">
        <td class="expand-cell">
          <span class="expand-icon">{{ selectedId === log.id ? '▼' : '▶' }}</span>
        </td>
        <td><app-status-tag [status]="statusLabel(log.status)" /></td>
        <td class="template-id-cell">{{ log.templateName || log.templateId }}</td>
        <td class="message-cell" [title]="log.messageSummary || ''">
          {{ log.messageSummary || '—' }}
        </td>
        <td class="correlation-cell">
          <code>{{ log.correlationId || '—' }}</code>
        </td>
        <td>{{ log.timestamp | date: 'MMM d, y HH:mm:ss' }}</td>
        <td class="ms-cell">{{ log.durationMs }} ms</td>
        <td>{{ log.source || '—' }}</td>
        <td class="muted">{{ log.expiresAt | date: 'mediumDate' }}</td>
      </tr>
      <!-- Expanded detail row -->
      @if (selectedId === log.id) {
        <tr class="detail-row">
          <td colspan="9">
            @if (!detailLoading) {
              <div class="detail-panel">
                @if (detailError) {
                  <div class="alert alert-error">{{ detailError }}</div>
                }
                @if (detail) {
                  <div class="detail-grid">
                    @if (parsedErrors.length > 0) {
                      <div class="detail-section error-section">
                        <div class="section-title error-title">Errors ({{ parsedErrors.length }})</div>
                        <div class="error-list">
                          @for (e of parsedErrors; track e) {
                            <div class="error-item">
                              <strong>{{ e.errorCode }}</strong>
                              @if (e.sourcePath) {
                                <span class="path-chip">{{ e.sourcePath }} → {{ e.fieldPath }}</span>
                              }
                              <span class="error-msg">{{ e.message }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    @if (parsedWarnings.length > 0) {
                      <div class="detail-section warning-section">
                        <div class="section-title warning-title">Warnings ({{ parsedWarnings.length }})</div>
                        <div class="error-list">
                          @for (w of parsedWarnings; track w) {
                            <div class="error-item">
                              <strong>{{ w.code }}</strong>
                              @if (w.sourcePath) {
                                <span class="path-chip warning-chip">{{ w.sourcePath }} → {{ w.targetPath }}</span>
                              }
                              <span class="error-msg">{{ w.message }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                    <div class="detail-section">
                      <div class="section-title">Input JSON</div>
                      <pre class="json-block">{{ detail.inputData | prettyJson }}</pre>
                    </div>
                    <div class="detail-section">
                      <div class="section-title">Output JSON</div>
                      @if (detail.outputData) {
                        <pre class="json-block">{{ detail.outputData | prettyJson }}</pre>
                      }
                      @if (!detail.outputData) {
                        <span class="muted">No output produced</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }
            @if (detailLoading) {
              <div class="loading-detail">Loading…</div>
            }
          </td>
        </tr>
      }
    </ng-template>
  </app-data-table>
</div>
